/* Application state. Reads IndexedDB once, then keeps marks fresh under the
   policy in §3.4 and recomputes the derived book on every change. */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef,
  type ReactNode,
} from 'react';
import * as api from '../api/client';
import { Backoff } from '../api/client';
import * as store from '../data/db';
import type { BookData, Settings } from '../data/db';
import { deriveBook, nextRefreshMs, toUsd, worthRecording, type Book } from './derive';
import { REBUILD_GENERATION, rebuildHistory as rebuild, yahooRangeCovering } from './rebuild';
import { mergeMarks, pruneMarks } from './marks';
import { stockPerpSymbol } from './offHours';
import { coinOf, coinPerpSymbol } from './assetKind';
import { setAmountsHidden } from '../lib/format';
import { applyTrades } from './importTrades';
import { closePortion, undoLastClose } from './closePerp';
import { convertCash as applyConversion } from './convertCash';
import {
  correctTransfer as applyCorrection, joinLots, reverseTransfer as applyReversal,
  transferShares as applyTransfer,
} from './transferShares';
import { newId } from '../lib/id';
import { runRules, type Alert } from '../rules/engine';
import { moverSentence, movers, type Mover } from './moveAlerts';
import { reminderSentence, remindersDue, type Reminder } from './reminders';
import { reconcileAlertLog } from './alertLog';
import { mergeLimits, type Limits } from '../rules/limits';
import { applyGlowStrength, applyTheme } from '../styles/themes';
import { lotsOutOfStep } from './basisFromTrades';
import type {
  CashFlow, EarningsDate, FxRates, Holding, Logo, PerpMark, Quote, Snapshot, Thesis, Trade,
} from '../types';

/* What a refused transfer says. One place, because the same six reasons come
   back from recording, correcting and deleting, and three copies of these
   sentences would be three chances to word one of them differently. */
function whyRefused(out: { refusal: string; short?: { account: string; has: number; needs: number } }): string {
  if (out.refusal === 'destination-short' && out.short) {
    return `That account now holds ${out.short.has} of the ${out.short.needs} this moved there, `
      + 'so they cannot all be put back. Correct the lots directly instead.';
  }
  switch (out.refusal) {
    case 'nothing-to-move': return 'Enter how many shares moved.';
    case 'same-account': return 'They are already in that account.';
    case 'no-lot': return 'That account does not hold them.';
    case 'not-enough-shares': return 'That is more than the account holds.';
    case 'futures-destination': return 'A futures wallet holds contracts, not shares.';
    case 'currency-mismatch':
      return 'That account holds this company in another currency.';
    default: return 'That could not be recorded.';
  }
}

interface State {
  ready: boolean;
  data: BookData;
  quotes: Quote[];
  earnings: EarningsDate[];
  perpMarks: PerpMark[];
  /** Company marks, keyed by fetch symbol. Cached bytes, not links — see
      `types.ts`. */
  logos: Logo[];
  fx: FxRates | undefined;
  settings: Settings;
  refreshing: boolean;
  /** Set when the last refresh failed. Marks stay on screen regardless (§3.3). */
  lastRefreshError: string | null;
}

const EMPTY: BookData = {
  accounts: [], dividends: [], holdings: [], theses: [], trades: [], cashflows: [],
  snapshots: [], perps: [], cash: [], themes: [], caps: [], watchlist: [], importBatches: [],
  alertLog: [], conversions: [], transfers: [], capBands: [],
};

type Action =
  | { type: 'loaded'; data: BookData; quotes: Quote[]; earnings: EarningsDate[]; perpMarks: PerpMark[]; logos: Logo[]; fx: FxRates | undefined; settings: Settings }
  | { type: 'data'; data: BookData }
  | { type: 'quotes'; quotes: Quote[] }
  | { type: 'earnings'; earnings: EarningsDate[] }
  | { type: 'perpMarks'; perpMarks: PerpMark[] }
  | { type: 'logos'; logos: Logo[]; drop?: string }
  | { type: 'fx'; fx: FxRates }
  | { type: 'settings'; settings: Settings }
  | { type: 'refreshing'; value: boolean }
  | { type: 'refreshError'; message: string | null };

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case 'loaded':
      return { ...s, ready: true, data: a.data, quotes: a.quotes, earnings: a.earnings,
               perpMarks: a.perpMarks, logos: a.logos, fx: a.fx, settings: a.settings };
    case 'data': return { ...s, data: a.data };
    /* Merged by symbol rather than replaced: a batch answering for three names
       must not drop the marks already held for the rest. */
    case 'logos': {
      const bySymbol = new Map(s.logos.map((l) => [l.symbol, l]));
      if (a.drop) bySymbol.delete(a.drop);
      for (const l of a.logos) bySymbol.set(l.symbol, l);
      return { ...s, logos: [...bySymbol.values()] };
    }
    // A failed symbol keeps whatever mark it already had, so a partial outage
    // degrades one row rather than the screen (§3.3).
    case 'quotes': return { ...s, quotes: mergeMarks(s.quotes, a.quotes) };
    case 'earnings': return { ...s, earnings: a.earnings };
    case 'perpMarks': return { ...s, perpMarks: a.perpMarks };
    case 'fx': return { ...s, fx: a.fx };
    case 'settings': return { ...s, settings: a.settings };
    case 'refreshing': return { ...s, refreshing: a.value };
    case 'refreshError': return { ...s, lastRefreshError: a.message };
  }
}

const initial: State = {
  ready: false, data: EMPTY, quotes: [], earnings: [], perpMarks: [], logos: [],
  fx: undefined,
  settings: { displayCurrency: 'USD', cycleIsHot: false, seeded: false, backupAcknowledged: false },
  refreshing: false, lastRefreshError: null,
};

export interface StoreApi {
  state: State;
  book: Book;
  alerts: Alert[];
  /** Companies that moved further than the threshold in settings. */
  movers: Mover[];
  /** Reports and ex-dates inside the window set in settings. */
  reminders: Reminder[];
  /** File defaults merged with whatever the settings screen has overridden. */
  limits: Limits;
  refresh: (opts?: { full?: boolean }) => Promise<void>;
  reload: () => Promise<void>;
  saveSettings: (patch: Partial<Settings>) => Promise<void>;
  putHolding: (h: Holding) => Promise<void>;
  /** Writes a contract without touching cash. Corrections only — restating a
      figure is not posting or releasing margin. */
  putPerp: (p: import('../types').Perp) => Promise<void>;
  /** Opens a contract: writes it and takes its margin out of the futures
      wallet, because posting margin is where the money goes. */
  openPerp: (p: import('../types').Perp) => Promise<void>;
  /** Adds to an open contract and posts the extra margin. */
  addToPerp: (p: import('../types').Perp, addedMarginUsdt: number) => Promise<void>;
  /** Closes a contract, whole or in part, at the figure the exchange actually
      gave you. The released margin and that result go back to the futures
      wallet; a contract closed out keeps its row, marked. */
  closePerp: (
    id: string,
    resultUsdt: number,
    portion?: number,
    opts?: {
      /* The exchange has already put the margin and the result back, and the
         book's balance was read from the exchange after it did. Closing here is
         then a record of something that has happened, not the thing happening,
         so it must not move the balance a second time. */
      walletAlreadySettled?: boolean;
    },
  ) => Promise<void>;
  /** Takes the most recent close on a contract back: the event, the margin, the
      notional, the realised figure and the money, all as they were. A contract
      closed out comes back open. */
  undoPerpClose: (id: string, opts?: { moveWallet?: boolean }) => Promise<void>;
  putWatch: (w: import('../types').Watch) => Promise<void>;
  removeWatch: (id: string) => Promise<void>;
  /** Files marks the app already holds — the quote that confirmed a symbol, for
      one — rather than throwing them away and waiting on the next scheduled
      refresh. With the market shut that wait is until tomorrow, which is a long
      time for a card to sit there reading "no price" about a name whose price
      just came back. */
  cacheQuotes: (rows: Quote[]) => Promise<void>;
  removeHolding: (id: string) => Promise<void>;
  putThesis: (t: Thesis) => Promise<void>;
  /** Clears the written thesis off a position. The record is the whole of what
      the book knows about why it is held, so this is a deletion rather than a
      blanking: leaving an empty one behind would read as a thesis that says
      nothing, which is a different claim from none. */
  removeThesis: (ticker: string) => Promise<void>;
  putTrade: (t: Trade) => Promise<void>;
  /** A mark the user chose, for a name the service has none for — or in place
      of one it has. Stored like a fetched mark so nothing downstream has to
      know the difference. */
  setLogo: (symbol: string, dataUrl: string) => Promise<void>;
  /** Forgets it. The next load asks the service again, which is what somebody
      clearing their own picture is asking for. */
  clearLogo: (symbol: string) => Promise<void>;
  /** Asks for marks the store has no answer for yet. Used by the search box,
      where the symbols are ones nobody holds — the book cannot know to want
      them in advance, and a list of names is easier to pick from with the marks
      on it. Silent about failure: a search result without a mark is a search
      result. */
  wantLogos: (symbols: string[]) => void;
  /** Records a broker's file and moves the positions to match, as one reversible
      run. Returns what it did. */
  runImport: (input: {
    account: import('../types').Account;
    fileName: string;
    trades: Trade[];
    columns: import('../types').TradeColumns;
  }) => Promise<{
    recorded: number; opened: number; closed: number;
    problems: Array<{ ticker: string; why: string }>;
  }>;
  /** Puts the book back exactly as it stood before that run. */
  undoImport: (batchId: string) => Promise<void>;
  putCashFlow: (c: CashFlow) => Promise<void>;
  /** A whole history at once. One reload at the end rather than one per row:
      a file of two hundred deposits through `putCashFlow` rebuilds the book two
      hundred times, and the book is every position, price and contract in it. */
  putCashFlows: (flows: CashFlow[]) => Promise<number>;
  /** Records a currency exchange and moves both balances in one go. Never a
      cash flow — the money paid in does not change when its currency does. */
  convertCash: (
    c: import('../types').CashConversion,
    opts?: import('./convertCash').ConvertOptions,
  ) => Promise<void>;
  /** Moves shares from one account to another. Committed as one swap of the
      whole book, because the two lot writes and the fee have to land together
      or not at all — see `state/transferShares.ts`. Returns what it did, or
      null when the move was refused. */
  transferShares: (
    req: import('./transferShares').TransferRequest,
  ) => Promise<import('./transferShares').TransferOutcome | null>;
  /** Writes a size ceiling. The id is the key and a rename never touches it —
      every assignment, every seeded book and every export names one by id. */
  putCapBand: (b: import('../types').CapBandRecord) => Promise<void>;
  /** Removes one. Refuses while anything is assigned to it: the caller has to
      have moved or cleared those first. */
  removeCapBand: (id: string) => Promise<boolean>;
  /** Points every assignment naming `from` at `to`, or clears them when `to` is
      null. One write, so a half-moved set is not a state that can exist. */
  reassignCapBand: (from: string, to: string | null) => Promise<number>;
  /** Joins duplicate rows for one ticker in one account back into the single
      row the model allows. */
  joinDuplicateLots: (ticker: string, account: string) => Promise<boolean>;
  /** Restates a transfer: puts the shares back, then moves what it now says.
      Refuses where the destination no longer holds what it put there. */
  correctTransfer: (
    record: import('../types').ShareTransfer,
    next: Omit<import('./transferShares').TransferRequest, 'id'>,
  ) => Promise<{ ok: true } | { ok: false; why: string }>;
  /** Deletes one, putting the shares and the fee back. Same refusal. */
  removeTransfer: (
    record: import('../types').ShareTransfer,
  ) => Promise<{ ok: true } | { ok: false; why: string }>;
  removeConversion: (id: string) => Promise<void>;
  putSnapshot: (s: Snapshot) => Promise<void>;
  /** A hand-filled past in one go, for the same reason `putCashFlows` exists. */
  putSnapshots: (rows: Snapshot[]) => Promise<number>;
  removeRecord: (store: Parameters<typeof import('../data/db').remove>[0], key: string) => Promise<void>;
  replaceBook: (data: BookData) => Promise<void>;
  setCash: (account: string, amount: number, currency: string) => Promise<void>;
  setThemes: (themes: BookData['themes']) => Promise<void>;
  putAccount: (a: import('../types').AccountRecord) => Promise<void>;
  removeAccount: (id: string) => Promise<void>;
  /** Reads the income statement for one ticker, from cache when it is fresh. */
  loadFinancials: (symbol: string) => Promise<import('../types').Financials | null>;
  /** Per-share dividend history, for proposing entries. */
  loadDividendHistory: (symbol: string) => Promise<import('../types').DividendHistory | null>;
  putDividend: (d: import('../types').Dividend) => Promise<void>;
  /* Daily closes for one symbol. Two years, cached for a day: the whole span
     every range can ask for, fetched once.
     *
     * Was `loadBenchmark`, and the name outlived its one caller — the position
     * screen now draws a company's own price from the same closes. Nothing
     * about it was ever specific to a comparison line. */
  loadDailyCloses: (symbol: string) => Promise<Array<{ date: string; close: number }>>;
  /* Finer bars over a short window, for the ranges a daily close cannot draw —
     one point is not a day's trading. Cached for minutes rather than a day,
     because a five-minute bar of a live session is stale almost at once. */
  loadIntraday: (
    symbol: string, range: string, interval: string,
  ) => Promise<Array<{ date: string; close: number }>>;
  /** Fills the chart backwards from real closing prices. Returns what it did. */
  rebuildHistory: (range?: string) => Promise<{
    added: number; missing: string[]; from: string | null; to: string | null; carriedDays: number;
  }>;
  /** Throws away everything the rebuild added, leaving the measurements. */
  clearRebuiltHistory: () => Promise<number>;
  removeDividend: (id: string) => Promise<void>;
  setCap: (ticker: string, capKind: BookData['caps'][number]['capKind'] | null) => Promise<void>;
}

const Ctx = createContext<StoreApi | null>(null);

const MINUTE = 60_000;

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initial);

  /* Thrown before any child formats a figure. The provider renders first, so by
     the time a screen calls `money()` the switch already reads what settings
     say — see the note in lib/format.ts for why this is a module flag and not a
     prop. Derived entirely from state, so running it on every render costs
     nothing and cannot drift from the setting. */
  setAmountsHidden(state.settings.hideAmounts === true);

  const backoff = useRef(new Backoff());
  const inFlight = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    const [data, settings, quotes, earnings, perpMarks, logos, fx] = await Promise.all([
      store.loadBook(), store.getSettings(), store.allQuotes(),
      store.allEarnings(), store.allPerpMarks(), store.allLogos(), store.getFx(),
    ]);

    /* Drop marks for symbols nothing asks about any more — a company sold and
       removed, a name taken off the radar, a ticker typed into the search box
       and never added. Nothing refetches them, so they sit in the store with a
       frozen timestamp getting older by the hour. Done on load, where the set
       of symbols the book cares about is settled, rather than after a fetch,
       where a quote just cached to confirm a new symbol would be caught. */
    const wanted = [
      ...data.holdings.map((h) => h.yahooSymbol),
      ...data.watchlist.map((w) => w.yahooSymbol),
    ];
    const kept = pruneMarks(quotes, wanted);
    if (kept.length !== quotes.length) {
      const keptSymbols = new Set(kept.map((q) => q.symbol));
      await store.dropQuotes(quotes.filter((q) => !keptSymbols.has(q.symbol)).map((q) => q.symbol));
    }

    /* And the same for company marks, for the same reason and with one
       exception. Searching for something and not buying it leaves its mark in
       the store, and a year of idle curiosity is a store full of pictures of
       companies nobody holds. A mark the user chose themselves is never
       dropped: they may be about to buy the thing they just made a mark for,
       and a picked file cannot be fetched again. */
    const wantedSet = new Set(wanted);
    const orphanLogos = logos.filter((l) => !l.custom && !wantedSet.has(l.symbol));
    const keptLogos = orphanLogos.length === 0
      ? logos
      : logos.filter((l) => l.custom || wantedSet.has(l.symbol));
    for (const l of orphanLogos) await store.removeLogo(l.symbol);

    /* Bring any lot whose average has fallen behind its own trades back into
       line. Here rather than only when a trade is saved: a trade corrected
       before this existed would otherwise leave the position reporting the
       average from before the correction for ever, which is exactly what it
       did. Writes only where they disagree, so every load after the first is a
       no-op — and only where the trades account for the whole lot, which is
       what makes the stored figure a summary of them. */
    const restated = lotsOutOfStep(data.trades, data.holdings);
    if (restated.length > 0) {
      for (const h of restated) await store.put('holdings', h);
      const byId = new Map(restated.map((h) => [h.id, h]));
      data.holdings = data.holdings.map((h) => byId.get(h.id) ?? h);
    }

    dispatch({ type: 'loaded', data, quotes: kept, earnings, perpMarks, logos: keptLogos, fx, settings });
  }, []);

  /* ---- first paint: seed if needed, then render from cache before any fetch ---- */
  useEffect(() => {
    void (async () => {
      // Ask before anything is written, so the very first seed lands in storage
      // the browser has agreed not to evict.
      await store.requestDurableStorage();
      await store.seedIfEmpty();
      await reload();
    })();
  }, [reload]);

  const limits = useMemo(
    () => mergeLimits(state.settings.limitOverrides),
    [state.settings.limitOverrides],
  );

  // Painting the theme from an effect would show the default for a frame first.
  useEffect(() => { applyTheme(state.settings.theme); }, [state.settings.theme]);
  useEffect(() => { applyGlowStrength(state.settings.glowStrength); }, [state.settings.glowStrength]);

  const book = useMemo(
    () => deriveBook({
      holdings: state.data.holdings,
      quotes: state.quotes,
      theses: state.data.theses,
      themes: state.data.themes,
      caps: state.data.caps,
      capBands: state.data.capBands,
      cash: state.data.cash,
      perps: state.data.perps,
      perpMarks: state.perpMarks,
      cashflows: state.data.cashflows,
      snapshots: state.data.snapshots,
      fx: state.fx,
      limits,
      accountRecords: state.data.accounts,
      useOffHoursMarks: state.settings.offHoursMarks !== false,
    }),
    [state.data, state.quotes, state.perpMarks, state.fx, limits, state.settings.offHoursMarks],
  );

  /* What the market did, kept apart from what your limits say. */
  const moves = useMemo(() => movers({
    quotes: state.quotes,
    positions: book.positions,
    watchlist: state.data.watchlist,
    thresholdPct: state.settings.moveAlertPct ?? 0,
    ...(state.settings.mutedMovers ? { muted: state.settings.mutedMovers } : {}),
  }), [state.quotes, book.positions, state.data.watchlist,
       state.settings.moveAlertPct, state.settings.mutedMovers]);

  const reminders = useMemo(() => remindersDue({
    positions: book.positions,
    watchlist: state.data.watchlist,
    earnings: state.earnings,
    withinDays: state.settings.remindDaysAhead ?? 0,
    planOver: limits.planBeforeEarningsOver,
  }), [book.positions, state.data.watchlist, state.earnings,
       state.settings.remindDaysAhead, limits.planBeforeEarningsOver]);

  /* Announced once per symbol per session.
   *
   * Only while the app is open, and that is a limit of the design rather than
   * an oversight: §3.4 says never refresh in the background, so there is no
   * moment when the app is shut and still knows a price moved. Delivering these
   * to a closed phone would mean a server holding your positions and watching
   * them for you, which is the one thing §1 rules out. */
  const announced = useRef(new Set<string>());
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    for (const m of moves) {
      if (announced.current.has(m.symbol)) continue;
      announced.current.add(m.symbol);
      try {
        new Notification(m.label, { body: moverSentence(m), tag: `move-${m.symbol}` });
      } catch { /* a shell that has no notifications still shows the strip */ }
    }
    for (const r of reminders) {
      if (announced.current.has(r.key)) continue;
      announced.current.add(r.key);
      try {
        new Notification(r.label, { body: reminderSentence(r), tag: r.key });
      } catch { /* the strip on the book still carries it */ }
    }
  }, [moves, reminders]);

  const alerts = useMemo(
    () => runRules({
      book,
      earnings: state.earnings,
      trades: state.data.trades,
      cycleIsHot: state.settings.cycleIsHot,
      limits,
      ...(state.settings.checksOff ? { checksOff: state.settings.checksOff } : {}),
      ...(state.settings.silencedChecks ? { silenced: state.settings.silencedChecks } : {}),
      capBands: state.data.capBands,
      ...(state.settings.customRules ? { customRules: state.settings.customRules } : {}),
    }),
    [
      book, state.earnings, state.data.trades, state.settings.cycleIsHot, limits,
      state.settings.checksOff, state.settings.silencedChecks, state.settings.customRules,
      state.data.capBands,
    ],
  );

  /* ---- company marks ----
   *
   * Fetched once per symbol and then never again: a logo does not move, and the
   * answer "there is none" is stored like any other answer so a name without
   * one is not asked about on every load for ever. Nothing here is on the
   * refresh path — a mark has no timestamp worth chasing.
   *
   * The request goes to the Worker, which asks the logo service. That is the
   * point rather than a convenience: asking from the phone would hand a third
   * party the list of what is held, one name at a time, against the phone's own
   * address. */
  const logosWanted = useMemo(() => {
    if (!state.ready || state.settings.showLogos === false) return [];
    const have = new Set(state.logos.map((l) => l.symbol));
    return [...new Set([
      ...state.data.holdings.map((h) => h.yahooSymbol),
      ...state.data.watchlist.map((w) => w.yahooSymbol),
    ])].filter((symbol) => symbol && !have.has(symbol));
  }, [state.ready, state.settings.showLogos, state.logos, state.data.holdings, state.data.watchlist]);

  const logosKey = logosWanted.join(',');
  useEffect(() => {
    if (logosKey === '') return;
    let live = true;
    void api.fetchLogos(logosKey.split(',')).then(async ({ rows }) => {
      if (!live || rows.length === 0) return;
      await store.saveLogos(rows);
      dispatch({ type: 'logos', logos: rows });
    }).catch(() => { /* no mark is a monogram, which is what was there before */ });
    return () => { live = false; };
  }, [logosKey]);

  /* ---- refresh ---- */

  const refresh = useCallback(async (opts: { full?: boolean } = {}) => {
    /* Watched companies are fetched alongside owned ones. They carry no weight
       and reach no total, but a radar with no prices on it is a list, and the
       reporting date is most of why the name is on there at all. */
    const symbols = [...new Set([
      ...state.data.holdings.map((h) => h.yahooSymbol),
      ...state.data.watchlist.map((w) => w.yahooSymbol),
    ])];
    /* The user's own contracts, plus the contract MEXC lists on each company
       held. The second set is what keeps the book moving while New York is
       shut — see `state/offHours.ts`. The board is fetched whole and filtered
       either way, so asking for more symbols costs nothing, and a ticker with
       no contract simply does not come back. */
    const perpSymbols = [...new Set([
      ...state.data.perps.map((p) => p.symbol),
      ...state.data.holdings.map((h) => stockPerpSymbol(h.ticker)),
      /* And the contract on each coin held, which is what prices the ones
         Yahoo does not list. Same request, no extra call — the board comes
         back whole and is filtered either way. */
      ...state.data.holdings
        .filter((h) => h.assetKind === 'crypto')
        .map((h) => coinPerpSymbol(coinOf(h.yahooSymbol))),
    ])];
    if (symbols.length === 0 && perpSymbols.length === 0) return;

    inFlight.current?.abort();
    const ctrl = new AbortController();
    inFlight.current = ctrl;
    dispatch({ type: 'refreshing', value: true });

    try {
      const [quotes, perps] = await Promise.all([
        api.fetchQuotes(symbols, ctrl.signal),
        api.fetchPerpMarks(perpSymbols, ctrl.signal),
      ]);
      if (ctrl.signal.aborted) return;

      if (quotes.rows.length) {
        await store.saveQuotes(quotes.rows);
        dispatch({ type: 'quotes', quotes: quotes.rows });
      }
      if (perps.rows.length) {
        await store.savePerpMarks(perps.rows);
        dispatch({ type: 'perpMarks', perpMarks: perps.rows });
      }

      const everythingFailed =
        quotes.rows.length === 0 && perps.rows.length === 0 &&
        (quotes.failed.length > 0 || perps.failed.length > 0);
      if (everythingFailed) throw new Error('No marks came back');

      dispatch({ type: 'refreshError', message: quotes.failed.length
        ? `No fresh mark for ${quotes.failed.join(', ')}`
        : null });
      backoff.current.succeed();
    } catch (e) {
      if (!ctrl.signal.aborted) {
        backoff.current.fail();
        dispatch({
          type: 'refreshError',
          message: e instanceof Error ? e.message : 'Could not reach the price service',
        });
      }
    } finally {
      if (!ctrl.signal.aborted) dispatch({ type: 'refreshing', value: false });
    }

    // FX every 6h and the calendar every 24h — both change far more slowly than
    // marks, so they ride along rather than driving their own timers (§3.2).
    void refreshSlow(state.fx, state.earnings, symbols, opts.full ?? false, dispatch);
  }, [state.data.holdings, state.data.watchlist, state.data.perps, state.fx, state.earnings]);

  /* ---- on app open, always (§3.4) ---- */
  const openedRef = useRef(false);
  useEffect(() => {
    if (!state.ready || openedRef.current) return;
    openedRef.current = true;
    void refresh({ full: true });
  }, [state.ready, refresh]);

  /* The marks as they stand right now, for the scheduler to read.
   *
   * The timer is set up once per session change and then reschedules itself
   * after each refresh, so anything it closes over is frozen at the moment the
   * effect ran. The session windows live on the quotes and are exactly the
   * thing that arrives *later* — a book opened after a night away starts with
   * yesterday's windows, every edge of them in the past, and would go on
   * reading them after the first fetch brought today's. A ref is read at call
   * time instead. */
  const quotesRef = useRef(state.quotes);
  quotesRef.current = state.quotes;

  /* A coin in the book means the night is not idle — see `refreshPeriodMs`. */
  const holdsCrypto = book.positions.some((p) => p.assetKind === 'crypto');

  /* ---- interval while foregrounded; never in the background (§3.4) ---- */
  useEffect(() => {
    if (!state.ready) return;
    let timer: number | undefined;

    /* §3.4's cadence, cut short when the exchange's own next window opens
       sooner — see `nextRefreshMs`. The pulse while shut is never zero, and the
       wake lands on the opening rather than up to a quarter of an hour after
       it. */
    const period = () =>
      nextRefreshMs(
        book.session.state, quotesRef.current, backoff.current.isBackingOff,
        Date.now(), holdsCrypto,
      );

    const schedule = () => {
      window.clearTimeout(timer);
      const ms = period();
      if (ms === 0 || document.visibilityState !== 'visible') return;
      timer = window.setTimeout(() => { void refresh().finally(schedule); }, ms);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();   // catch up on whatever moved while we were away
        schedule();
      } else {
        window.clearTimeout(timer);
      }
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [state.ready, book.session.state, holdsCrypto, refresh]);

  /* ---- a first device starts with four dots and three dashes, which reads as
          a broken chart rather than a young one. Once marks are in, everything
          back to the day the book began is filled from closing prices — tagged
          as rebuilt, drawn without dots, and removable in settings.

          It runs once, and then once more after the rebuild itself improves:
          a device filled by an older version would otherwise keep that older,
          narrower line until somebody thought to visit settings. What it never
          does is touch a measurement, or run again on a line this version
          already drew. ---- */
  const backfilled = useRef(false);
  useEffect(() => {
    if (!state.ready || backfilled.current) return;
    if (state.quotes.length === 0 || state.data.holdings.length === 0) return;

    const rebuilt = state.data.snapshots.some((s) => s.reconstructed);
    const current = state.settings.rebuiltWith === REBUILD_GENERATION;
    if (rebuilt && current) { backfilled.current = true; return; }

    backfilled.current = true;
    void (async () => {
      try { await api_.rebuildHistory(); } catch { /* the seeded points still show */ }
    })();
  });

  /* ---- a breach that gets fixed should leave a trace ----
   *
   * Written on a change only: the reconciliation returns the episodes that
   * actually moved, so a book sitting still writes nothing. */
  useEffect(() => {
    if (!state.ready) return;
    const changed = reconcileAlertLog(alerts, state.data.alertLog);
    if (changed.length === 0) return;
    void (async () => {
      for (const episode of changed) await store.put('alertLog', episode);
      await reload();
    })();
  }, [state.ready, alerts, state.data.alertLog, reload]);

  /* ---- one snapshot per app open, at most one per hour (§9.2) ---- */
  const snapshotWritten = useRef(false);
  useEffect(() => {
    if (!state.ready || snapshotWritten.current) return;
    if (book.totals.totalUsd <= 0 || state.quotes.length === 0) return;

    /* A book with part of itself still carried at cost is not a measurement of
       the book. The seed ships marks for three of twelve names, so this used to
       fire the moment those three loaded and write the other nine at their cost
       basis into the permanent record — a drop of thousands, at the right-hand
       end of every chart, that no market ever produced.

       So it waits: not while a refresh is still in flight, and not while any
       position is unpriced. The cost is that a symbol which never answers stops
       history being recorded at all — but that is loud rather than quiet, since
       the header reads "part at cost" the whole time it is true. A missing
       point can be filled later. A false one gets believed. */
    if (!worthRecording(book.totals, state.refreshing)) return;

    snapshotWritten.current = true;
    void (async () => {
      const latest = state.data.snapshots
        .map((s) => new Date(s.at).getTime())
        .reduce((a, b) => Math.max(a, b), 0);
      if (Date.now() - latest < 60 * MINUTE) return;
      const snap: Snapshot = {
        at: new Date().toISOString(),
        /* On regular-hours marks, not on whatever is printing after the bell.
           The two are the same number during the session; outside it, the
           extended print is worth showing and not worth recording (§9.1). */
        totalUsd: book.totals.totalAtRegularUsd,
        contributedUsd: book.totals.contributedUsd,
      };
      await store.put('snapshots', snap);
      await reload();
    })();
  }, [state.ready, state.refreshing, state.quotes.length, book.totals, state.data.snapshots, reload]);

  /* The futures wallet, and moving money in or out of it.
   *
   * Posting margin takes it out of free cash; closing puts it back. Those are
   * one movement seen from both ends, so both go through here. */
  const futuresAccount = () =>
    state.data.accounts.find((a) => a.isFutures) ?? null;

  const postMargin = async (amountUsdt: number) => {
    const account = futuresAccount();
    if (!account || !(amountUsdt > 0)) return;
    const held = state.data.cash.find((c) => c.account === account.id);
    await store.put('cash', {
      account: account.id,
      amount: (held?.amount ?? 0) - amountUsdt,
      currency: held?.currency ?? account.currency,
    } as never);
  };

  /* ---- mutations ---- */

  const after = useCallback(async (fn: () => Promise<void>) => {
    await fn();
    const data = await store.loadBook();
    dispatch({ type: 'data', data });
  }, []);

  const api_: StoreApi = useMemo(() => ({
    state, book, alerts, movers: moves, reminders, limits, refresh, reload,
    saveSettings: async (patch) => {
      dispatch({ type: 'settings', settings: await store.saveSettings(patch) });
    },
    putHolding: (h) => after(() => store.put('holdings', h)),
    /* Moves money between the futures wallet and margin. Negative posts it out
       of free cash, positive returns it. One place, so the two directions
       cannot drift apart. */
    putPerp: (p) => after(() => store.put('perps', p)),

    openPerp: (p) => after(async () => {
      await store.put('perps', p);
      await postMargin(p.marginUsdt);
    }),

    addToPerp: (p, addedMarginUsdt) => after(async () => {
      await store.put('perps', p);
      await postMargin(addedMarginUsdt);
    }),

    /* Margin posted comes out of the wallet.
     *
     * Closing a contract has always put its margin back; opening one never took
     * it out. So every close grew the free balance and nothing ever shrank it,
     * and the same money was counted twice over — once as margin on the
     * position, once as cash sitting free. A book whose realised profits went
     * straight into new positions reported them as still spare.
     *
     * These two are one movement seen from both ends and have to stay that
     * way: `closePerp` credits `margin + result`, this debits `margin`. */
    closePerp: async (id, resultUsdt, portion = 1, opts = {}) => {
      const perp = state.data.perps.find((p) => p.id === id);
      if (!perp || perp.closedAt) return;
      const outcome = closePortion(perp, portion, resultUsdt, undefined, opts.walletAlreadySettled);
      if (!outcome.next) return;

      /* The margin and whatever it came to go back to the wallet. Without this
         the book would drop by the whole position the moment it was closed —
         money that did not go anywhere except from one line to another. */
      const futures = state.data.accounts.find((a) => a.isFutures)
        ?? state.data.accounts.find((a) => a.id === perp.id.split('-')[0]);
      const held = futures
        ? state.data.cash.find((c) => c.account === futures.id)
        : undefined;

      /* Unless the exchange has already done it.
       *
       * A contract closed at MEXC has had its margin and its result put back
       * there, and the balance in this book was read from MEXC after that
       * happened — so crediting it here again counts the same money twice, and
       * the free balance that reconciled to the penny before the close is wrong
       * the moment it is recorded. The rule is the one this book applies to
       * every other record of something that already happened: write it down,
       * do not re-run it. */
      await after(async () => {
        await store.put('perps', outcome.next!);
        if (futures && !opts.walletAlreadySettled) {
          await store.put('cash', {
            account: futures.id,
            amount: (held?.amount ?? 0) + outcome.toWallet,
            currency: held?.currency ?? futures.currency,
          } as never);
        }
      });
    },
    /* The other end of closePerp: what that credited, this debits. Same wallet,
       same figure, so a close taken back leaves the balance where it started.
       `moveWallet` overrides what the close recorded, for closes written before
       that was kept — the screen asks rather than guessing. */
    undoPerpClose: async (id, opts = {}) => {
      const perp = state.data.perps.find((p) => p.id === id);
      if (!perp) return;
      const outcome = undoLastClose(perp);
      if (!outcome) return;

      const futures = state.data.accounts.find((a) => a.isFutures)
        ?? state.data.accounts.find((a) => a.id === perp.id.split('-')[0]);
      const held = futures
        ? state.data.cash.find((c) => c.account === futures.id)
        : undefined;
      const takeBack = opts.moveWallet === undefined
        ? outcome.fromWallet
        : opts.moveWallet ? outcome.event.releasedUsdt + outcome.event.resultUsdt : 0;

      await after(async () => {
        await store.put('perps', outcome.next);
        if (futures && takeBack !== 0) {
          await store.put('cash', {
            account: futures.id,
            amount: (held?.amount ?? 0) - takeBack,
            currency: held?.currency ?? futures.currency,
          } as never);
        }
      });
    },

    putWatch: (w) => after(() => store.put('watchlist', w)),
    removeWatch: (id) => after(() => store.remove('watchlist', id)),
    cacheQuotes: async (rows) => {
      if (rows.length === 0) return;
      await store.saveQuotes(rows);
      // Merged by symbol, so filing one mark never displaces the rest.
      dispatch({ type: 'quotes', quotes: rows });
    },
    removeHolding: (id) => after(() => store.remove('holdings', id)),
    putThesis: (t) => after(() => store.put('theses', t)),
    removeThesis: (ticker) => after(() => store.remove('theses', ticker)),
    putTrade: (t) => after(() => store.put('trades', t)),

    setLogo: async (symbol, dataUrl) => {
      const row = { symbol, dataUrl, custom: true, fetchedAt: new Date().toISOString() };
      await store.saveLogos([row]);
      dispatch({ type: 'logos', logos: [row] });
    },
    clearLogo: async (symbol) => {
      await store.removeLogo(symbol);
      dispatch({ type: 'logos', logos: [], drop: symbol });
    },
    wantLogos: (symbols) => {
      if (state.settings.showLogos === false) return;
      const have = new Set(state.logos.map((l) => l.symbol));
      const missing = [...new Set(symbols)].filter((s_) => s_ && !have.has(s_));
      if (missing.length === 0) return;
      void api.fetchLogos(missing).then(async ({ rows }) => {
        if (rows.length === 0) return;
        await store.saveLogos(rows);
        dispatch({ type: 'logos', logos: rows });
      }).catch(() => { /* a row without a mark is still a row */ });
    },

    runImport: async ({ account, fileName, trades, columns }) => {
      /* A ticker the book has never held needs its exact fetch symbol, and §3.2
         is explicit that the symbol is stored rather than guessed at call time.
         One batched lookup covers every new name in the file; anything the
         upstream will not answer for still gets a position, which then shows as
         unpriced — visible, unlike a symbol that is quietly wrong. */
      const held = new Set(
        state.data.holdings.filter((h) => h.account === account).map((h) => h.ticker),
      );
      const fresh = [...new Set(trades.map((t) => t.ticker))].filter((t) => !held.has(t));
      const resolved = new Map<string, import('../types').Quote>();
      if (fresh.length > 0) {
        try {
          const { rows } = await api.fetchQuotes(fresh);
          for (const q of rows) resolved.set(q.symbol.toUpperCase(), q);
        } catch { /* the positions still open, at the typed ticker */ }
      }

      const accountRecord = state.data.accounts.find((a) => a.id === account);
      const result = applyTrades(trades, state.data.holdings, account, {
        resolve: (ticker) => {
          const q = resolved.get(ticker);
          if (!q) return null;
          const out: import('./importTrades').Resolved = {
            yahooSymbol: q.symbol,
            currency: (q.currency as import('../types').Currency) || 'USD',
          };
          if (q.shortName) out.name = q.shortName;
          return out;
        },
        makeId: () => newId('hold'),
        fallbackCurrency: accountRecord?.currency ?? 'USD',
      });

      const batch: import('../types').ImportBatch = {
        id: newId('import'),
        account,
        fileName,
        at: new Date().toISOString(),
        tradeIds: result.trades.map((t) => t.id),
        holdingsBefore: result.before,
        createdHoldingIds: result.createdIds,
      };

      await after(async () => {
        for (const t of result.trades) await store.put('trades', t);
        for (const h of result.put) await store.put('holdings', h);
        for (const id of result.removeIds) await store.remove('holdings', id);
        if (accountRecord) await store.put('accounts', { ...accountRecord, tradeColumns: columns });
        await store.put('importBatches', batch);
      });

      return {
        recorded: result.trades.length,
        opened: result.createdIds.length,
        closed: result.removeIds.length,
        problems: result.problems,
      };
    },

    undoImport: async (batchId) => {
      const batch = state.data.importBatches.find((b) => b.id === batchId);
      if (!batch) return;
      await after(async () => {
        // Restore first, then remove: a holding that was emptied comes back from
        // `holdingsBefore`, and one this run invented goes entirely.
        for (const h of batch.holdingsBefore) await store.put('holdings', h);
        for (const id of batch.createdHoldingIds) await store.remove('holdings', id);
        for (const id of batch.tradeIds) await store.remove('trades', id);
        await store.remove('importBatches', batch.id);
      });
    },
    putCashFlow: (c) => after(() => store.put('cashflows', c)),

    putCashFlows: async (flows) => {
      if (flows.length === 0) return 0;
      await after(async () => {
        for (const f of flows) await store.put('cashflows', f);
      });
      return flows.length;
    },

    transferShares: async (req) => {
      const outcome = applyTransfer(state.data, state.data.accounts, req);
      if (!outcome) return null;
      /* One transaction. Sequential puts cannot promise this: a failure between
         the source write and the destination write loses or duplicates the
         shares permanently, and `after` reloads the book between each. */
      await after(() => store.replaceBook(outcome.data));
      return outcome;
    },

    correctTransfer: async (record, next) => {
      const out = applyCorrection(state.data, state.data.accounts, record, next);
      if ('refusal' in out) return { ok: false, why: whyRefused(out) };
      await after(() => store.replaceBook(out.data));
      return { ok: true };
    },

    removeTransfer: async (record) => {
      const out = applyReversal(state.data, record);
      if ('refusal' in out) return { ok: false, why: whyRefused(out) };
      await after(() => store.replaceBook(out.outcome.data));
      return { ok: true };
    },

    putCapBand: (b) => after(() => store.put('capBands', b)),

    removeCapBand: async (id) => {
      if (state.data.caps.some((c) => c.capKind === id)) return false;
      await after(() => store.remove('capBands', id));
      return true;
    },

    reassignCapBand: async (from, to) => {
      const rows = state.data.caps.filter((c) => c.capKind === from);
      if (rows.length === 0) return 0;
      await after(async () => {
        for (const row of rows) {
          if (to === null) await store.remove('caps', row.ticker);
          else await store.put('caps', { ...row, capKind: to });
        }
      });
      return rows.length;
    },

    joinDuplicateLots: async (ticker, account) => {
      const rows = state.data.holdings.filter(
        (h) => h.ticker === ticker && h.account === account,
      );
      const joined = joinLots(rows);
      if (!joined) return false;
      await after(async () => {
        for (const row of rows) if (row.id !== joined.id) await store.remove('holdings', row.id);
        await store.put('holdings', joined);
      });
      return true;
    },

    convertCash: (c, opts) => after(async () => {
      const outcome = applyConversion(state.data.cash, c, opts);
      if (!outcome) return;
      await store.put('conversions', c);
      for (const b of outcome.balances) {
        await store.put('cash', b as never);
      }
    }),

    /* Removing the record leaves the balances where they are. Undoing the
       movement as well would be guessing that nothing has happened to either
       account since, and a balance is typed over in one tap anyway. */
    removeConversion: (id) => after(() => store.remove('conversions', id)),
    putSnapshot: (s) => after(() => store.put('snapshots', s)),

    putSnapshots: async (rows) => {
      if (rows.length === 0) return 0;
      await after(async () => {
        for (const r of rows) await store.put('snapshots', r);
      });
      return rows.length;
    },
    removeRecord: (which, key) => after(() => store.remove(which, key)),
    replaceBook: (data) => after(() => store.replaceBook(data)),
    setCash: (account, amount, currency) =>
      after(() => store.put('cash', { account, amount, currency } as never)),
    setThemes: (themes) => after(async () => {
      const existing = await store.loadBook();
      for (const t of existing.themes) {
        if (!themes.some((n) => n.name === t.name)) await store.remove('themes', t.name);
      }
      for (const t of themes) await store.put('themes', t);
    }),
    loadFinancials: async (symbol) => {
      const cached = await store.getFinancials(symbol);
      // A quarter's numbers do not move once published; a day is generous.
      const DAY = 24 * 60 * MINUTE;
      if (cached && Date.now() - new Date(cached.fetchedAt).getTime() < DAY) return cached;
      try {
        const { rows } = await api.fetchFinancials([symbol]);
        if (rows.length === 0) return cached ?? null;
        await store.saveFinancials(rows);
        return rows[0] ?? null;
      } catch {
        // Whatever was cached is better than nothing, however old.
        return cached ?? null;
      }
    },
    loadDividendHistory: async (symbol) => {
      const cached = await store.getDividendHistory(symbol);
      const DAY = 24 * 60 * MINUTE;
      if (cached && Date.now() - new Date(cached.fetchedAt).getTime() < DAY) return cached;
      try {
        const { rows } = await api.fetchDividendHistory([symbol]);
        if (rows.length === 0) return cached ?? null;
        await store.saveDividendHistory(rows);
        return rows[0] ?? null;
      } catch {
        return cached ?? null;
      }
    },
    loadDailyCloses: async (symbol) => {
      const key = `closes:${symbol}`;
      const DAY = 24 * 60 * MINUTE;
      const cached = await store.getKv<{ at: string; days: Array<{ date: string; close: number }> }>(key);
      if (cached && Date.now() - new Date(cached.at).getTime() < DAY) return cached.days;
      try {
        const { rows } = await api.fetchHistory([symbol], '2y');
        const days = rows[0]?.days ?? [];
        if (days.length > 0) await store.setKv(key, { at: new Date().toISOString(), days });
        return days;
      } catch {
        // Yesterday's closes still draw the same shape.
        return cached?.days ?? [];
      }
    },

    loadIntraday: async (symbol, range, interval) => {
      const key = `bars:${symbol}:${range}:${interval}`;
      const cached = await store.getKv<{ at: string; days: Array<{ date: string; close: number }> }>(key);
      const ttl = interval === '1h' || interval === '30m' ? 5 * MINUTE : 2 * MINUTE;
      if (cached && Date.now() - new Date(cached.at).getTime() < ttl) return cached.days;
      try {
        const { rows } = await api.fetchHistory([symbol], range, undefined, interval);
        const days = rows[0]?.days ?? [];
        if (days.length > 0) await store.setKv(key, { at: new Date().toISOString(), days });
        return days;
      } catch {
        // The last bars still draw the same shape, a few minutes behind.
        return cached?.days ?? [];
      }
    },

    rebuildHistory: async (range) => {
      const symbols = [...new Set(state.data.holdings.map((h) => h.yahooSymbol))];

      /* The book starts on the earlier of its first recorded value and the
         oldest lot still held. Fetching further back costs nothing, but drawing
         further back would price today's shares in a month the account did not
         hold them, so the window is asked for and then cut to that date. */
      const startFrom = [
        ...state.data.holdings.map((h) => h.openedAt.slice(0, 10)),
        ...state.data.snapshots.filter((s) => !s.reconstructed).map((s) => s.at.slice(0, 10)),
      ].sort()[0];
      const asked = range ?? yahooRangeCovering(startFrom);

      const { rows } = await api.fetchHistory(symbols, asked);

      // Cash, margin posted and unrealised are held at today's figures: the
      // dates those positions opened and closed are not in the record, and
      // guessing them would put invented movement into the line.
      const nonEquityUsd = book.totals.cashUsd + book.totals.perpMarginUsd
        + book.totals.perpUnrealisedUsd;

      /* A name the upstream has no history for is held flat at what it is worth
         now, the same treatment cash gets. Dropping it instead would take its
         whole value out of every past point and draw a climb that never
         happened. */
      const pricedSymbols = new Set(
        rows.filter((r) => r.days && r.days.length > 0).map((r) => r.symbol),
      );
      const unpricedUsd = book.positions
        .filter((p) => p.holdings.some((h) => !pricedSymbols.has(h.yahooSymbol)))
        .reduce((sum, p) => sum + p.marketValue, 0);

      const result = rebuild({
        history: rows,
        holdings: state.data.holdings,
        nonEquityUsd,
        unpricedUsd,
        startFrom,
        usdPerUnit: (currency) => toUsd(1, currency, state.fx),
        existing: state.data.snapshots,
      });

      await after(async () => {
        // Replace any previous rebuild rather than layering one on another.
        for (const s of state.data.snapshots) {
          if (s.reconstructed) await store.remove('snapshots', s.at);
        }
        for (const snap of result.snapshots) await store.put('snapshots', snap);
      });
      /* Stamped so the automatic backfill knows this line is already current and
         leaves it alone until the rebuild itself changes again — but only once
         something was actually drawn. An upstream that answered with nothing is
         a bad afternoon, not a finished job, and marking the device current
         would mean never trying again. */
      if (result.snapshots.length > 0) {
        dispatch({
          type: 'settings',
          settings: await store.saveSettings({ rebuiltWith: REBUILD_GENERATION }),
        });
      }

      return {
        added: result.snapshots.length, missing: result.missing,
        from: result.from, to: result.to, carriedDays: result.carriedDays,
      };
    },
    clearRebuiltHistory: async () => {
      const rebuilt = state.data.snapshots.filter((s) => s.reconstructed);
      await after(async () => {
        for (const s of rebuilt) await store.remove('snapshots', s.at);
      });
      return rebuilt.length;
    },
    putDividend: (d) => after(() => store.put('dividends', d)),
    removeDividend: (id) => after(() => store.remove('dividends', id)),
    putAccount: (a) => after(() => store.put('accounts', a)),
    removeAccount: (id) => after(() => store.remove('accounts', id)),
    setCap: (ticker, capKind) => after(async () => {
      if (capKind === null) await store.remove('caps', ticker);
      else await store.put('caps', { ticker, capKind });
    }),
  }), [state, book, alerts, moves, reminders, limits, refresh, reload, after]);

  return <Ctx.Provider value={api_}>{children}</Ctx.Provider>;
}

async function refreshSlow(
  fx: FxRates | undefined,
  earnings: EarningsDate[],
  symbols: string[],
  force: boolean,
  dispatch: (a: Action) => void,
): Promise<void> {
  const SIX_HOURS = 6 * 60 * MINUTE;
  const DAY = 24 * 60 * MINUTE;

  const fxAge = fx ? Date.now() - new Date(fx.fetchedAt).getTime() : Infinity;
  if (force || fxAge > SIX_HOURS) {
    try {
      const next = await api.fetchFx();
      await store.saveFx(next);
      dispatch({ type: 'fx', fx: next });
    } catch {
      // The previous table stays in use and keeps its publication date label.
    }
  }

  const oldest = earnings.length
    ? Math.min(...earnings.map((e) => new Date(e.fetchedAt).getTime()))
    : 0;
  const missing = symbols.filter((s) => !earnings.some((e) => e.symbol === s));
  if (force || missing.length > 0 || Date.now() - oldest > DAY) {
    try {
      const { rows } = await api.fetchCalendar(symbols);
      if (rows.length) {
        await store.saveEarnings(rows);
        dispatch({ type: 'earnings', earnings: await store.allEarnings() });
      }
    } catch {
      // Earnings dates are a nicety; a failure here never blocks anything.
    }
  }
}

/** Convenience for components that only need the numbers. */
export function useLimits(): Limits {
  return useStore().limits;
}

/** The broker list, in display order. */
export function useAccounts() {
  return useStore().state.data.accounts;
}

export function useStore(): StoreApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useStore must be used inside StoreProvider');
  return ctx;
}
